import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { useLicense } from "../../context/licenseContext";
import { mixDisplayName } from "../../utils/mixDisplayName";
import { formatWeightFromGrams } from "../../utils/weightUtils";
import { getColor } from "../../utils/colorfunctions";
import ColorDot from "../../components/ColorDot/ColorDot";
import Loading from "../../components/Loading/Loading";
import { toSlug, BADGE_META } from "../../utils/badgeMeta";
import { QRCodeSVG } from "qrcode.react";
import "./MixDetail.css";
 
const safeNum = (v) => parseFloat(v) || 0;
const fmt = (v) => (v == null || isNaN(v) ? "—" : v.toFixed(2) + "%");

export default function MixDetail({ setHeaderTitle }) {
    const { productId, licenseId } = useParams(); 
    const navigate = useNavigate(); 
    const { currentLicense } = useLicense();
    const isOwner = parseInt(licenseId) === currentLicense?.id;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const badgeQrRef = useRef(null);

    useEffect(() => {
        if (setHeaderTitle) setHeaderTitle("Mix Detail");
    }, [setHeaderTitle]);

    useEffect(() => {
        setLoading(true);
        apiClient
            .get(`/mixes/${productId}/components`)
            .then((res) => setData(res.data))
            .catch((err) => {
                setError(
                    err.response?.status === 404
                        ? "Mix product not found."
                        : err.response?.status === 403
                        ? "You do not have access to this product."
                        : "Something went wrong."
                );
            })
            .finally(() => setLoading(false));
    }, [productId]);

    const handleDelete = async () => {
        try {
            await apiClient.delete(`/products/${productId}`);
            navigate(-1);
        } catch {
            alert("Failed to delete mix product.");
        }
    };

    const downloadBadge = async () => {
        if (!data?.product) return;
        const meta = BADGE_META["mix"];
        if (!meta) return;

        const qrEl = badgeQrRef.current?.querySelector("svg");
        if (!qrEl) return;

        const variant = meta.strain;
        const response = await fetch(`/Badges/${variant.file}`);
        const svgText = await response.text();

        const parser = new DOMParser();
        const badgeDoc = parser.parseFromString(svgText, "image/svg+xml");
        const badgeSvg = badgeDoc.querySelector("svg");

        const name = mixDisplayName(data.product.product_name, data.product.product_type, data.product.created_at);
        if (name) {
            const NS = "http://www.w3.org/2000/svg";
            const textEl = badgeDoc.createElementNS(NS, "text");
            textEl.setAttribute("x", "51.03");
            textEl.setAttribute("y", "27.5");
            textEl.setAttribute("text-anchor", "middle");
            textEl.setAttribute("dominant-baseline", "central");
            textEl.setAttribute("font-family", "Arial, Helvetica, sans-serif");
            textEl.setAttribute("font-weight", "bold");
            textEl.setAttribute("fill", "#231f20");
            const fontSize = name.length > 20 ? 3 : name.length > 12 ? 3.8 : 4.5;
            textEl.setAttribute("font-size", String(fontSize));
            textEl.textContent = name;
            badgeSvg.appendChild(textEl);
        }

        const qrClone = qrEl.cloneNode(true);
        qrClone.setAttribute('x', String(variant.qx));
        qrClone.setAttribute('y', String(variant.qy));
        qrClone.setAttribute('width', String(variant.qs));
        qrClone.setAttribute('height', String(variant.qs));
        badgeSvg.appendChild(badgeDoc.importNode(qrClone, true));

        const svgString = new XMLSerializer().serializeToString(badgeSvg);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name || "mix-badge"}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="mix-detail">
                <button className="mix-detail__back" onClick={() => navigate(-1)}>&larr; Back</button>
                <div className="mix-detail__error">{error}</div>
            </div>
        );
    }

    if (!data) return null;

    const { product, components } = data;
    const displayName = mixDisplayName(product.product_name, product.product_type, product.created_at);
    const typeLabel = product.product_type === "mix-preroll" ? "Mixed Pre-Roll" : "Mixed Flower";
    const totalWeight = product.quantity != null ? formatWeightFromGrams(safeNum(product.quantity)) : "—";
    const createdDate = product.created_at
        ? new Date(product.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
        : "—";

    const slug = currentLicense ? toSlug(currentLicense.company) : "";
    const qrUrl = slug ? `${window.location.origin}/compliance/${slug}?type=mix` : "";

    return (
        <div className="mix-detail">
            <button className="mix-detail__back" onClick={() => navigate(-1)}>&larr; Back</button>

            {/* Header */}
            <section className="coa-page__section mix-detail__header">
                <div className="mix-detail__header-top">
                    <div className="mix-detail__header-info">
                        <h1 className="mix-detail__name">{displayName}</h1>
                        <div className="mix-detail__badges">
                            <span className="mix-detail__type-badge">{typeLabel}</span>
                            <span className="mix-detail__type-badge mix-detail__type-badge--count">
                                {components.length} Component{components.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="mix-detail__meta-grid">
                    <div className="mix-detail__meta-item">
                        <span className="mix-detail__meta-label">Total Weight</span>
                        <span className="mix-detail__meta-value">{totalWeight}</span>
                    </div>
                    <div className="mix-detail__meta-item">
                        <span className="mix-detail__meta-label">Created</span>
                        <span className="mix-detail__meta-value">{createdDate}</span>
                    </div>
                    {product.sku && (
                        <div className="mix-detail__meta-item">
                            <span className="mix-detail__meta-label">SKU</span>
                            <span className="mix-detail__meta-value">{product.sku}</span>
                        </div>
                    )}
                    {product.price != null && (
                        <div className="mix-detail__meta-item">
                            <span className="mix-detail__meta-label">Price</span>
                            <span className="mix-detail__meta-value">${parseFloat(product.price).toFixed(2)}</span>
                        </div>
                    )}
                </div>
            </section>

            {/* Constituents */}
            <section className="coa-page__section">
                <h2 className="coa-page__section-title">Constituent Components</h2>
                <div className="mix-detail__comp-table-wrap">
                    <table className="mix-detail__comp-table">
                        <thead>
                            <tr>
                                <th>Color</th>
                                <th>Product Name</th>
                                <th>Ratio</th>
                                <th>Total THC</th>
                                <th>Contaminants</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {components.map((comp) => {
                                const name = comp.source_product_name || comp.coa_product_name || "—";
                                const ratio = comp.ratio_pct != null ? `${parseFloat(comp.ratio_pct).toFixed(1)}%` : "—";
                                const totalThc = comp.THCa != null ? fmt(safeNum(comp.THCa) * 0.877 + safeNum(comp.delta_9_THC)) : "—";
                                const colorKey = (comp.color || comp.coa_color || "").toLowerCase();

                                const safetyFields = ["microbials", "pesticides", "heavy_metals", "foreign_matter", "mycotoxins"];
                                const allPass = safetyFields.every((f) => typeof comp[f] === "string" && comp[f].toLowerCase().includes("pass"));
                                const anyFail = safetyFields.some((f) => typeof comp[f] === "string" && comp[f].toLowerCase().includes("fail"));
                                const contLabel = allPass ? "Pass" : anyFail ? "Fail" : "Not Tested";
                                const contClass = allPass ? "pass" : anyFail ? "fail" : "neutral";

                                return (
                                    <tr key={comp.component_id}>
                                        <td><ColorDot color={colorKey} /></td>
                                        <td className="mix-detail__comp-name">{name}</td>
                                        <td>{ratio}</td>
                                        <td>{totalThc}</td>
                                        <td>
                                            <span className={`mix-detail__safety mix-detail__safety--${contClass}`}>
                                                {contLabel}
                                            </span>
                                        </td>
                                        <td>
                                            {comp.coa_id && (
                                                <button
                                                    className="mix-detail__view-btn"
                                                    onClick={() => navigate(`/product/${comp.coa_id}/${licenseId}`)}
                                                >
                                                    View
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Badge & Actions */}
            {isOwner && (
                <section className="coa-page__section">
                    <h2 className="coa-page__section-title">Actions</h2>
                    <div className="mix-detail__actions">
                        {qrUrl && (
                            <button className="mix-detail__action-btn" onClick={downloadBadge}>
                                Download Badge
                            </button>
                        )}
                        {!confirmDelete ? (
                            <button
                                className="mix-detail__action-btn mix-detail__action-btn--danger"
                                onClick={() => setConfirmDelete(true)}
                            >
                                Delete Mix
                            </button>
                        ) : (
                            <div className="mix-detail__confirm-row">
                                <span>Are you sure?</span>
                                <button className="mix-detail__action-btn mix-detail__action-btn--danger" onClick={handleDelete}>
                                    Yes, Delete
                                </button>
                                <button className="mix-detail__action-btn" onClick={() => setConfirmDelete(false)}>
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Hidden QR for badge generation */}
            {qrUrl && (
                <div ref={badgeQrRef} style={{ position: "absolute", left: "-9999px" }}>
                    <QRCodeSVG
                        value={qrUrl}
                        size={108}
                        level="M"
                        includeMargin={false}
                        fgColor="#0f172a"
                        bgColor="#ffffff"
                    />
                </div>
            )}
        </div>
    );
}
